import {
  Box,
  Button,
  Container,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography
} from "@mui/material";
import { useEffect, useMemo, useRef, useState } from "react";
import { CSVLink } from "react-csv";
import { TailSpin } from "react-loader-spinner";
import { ToastContainer, toast } from "react-toastify";
import { adminAxiosInstance } from "src/api/axios";
import PageContainer from "src/components/container/PageContainer";

import "react-toastify/dist/ReactToastify.css"; // import first

function StatCard({ label, value }) {
  return (
    <Box
      p={2}
      borderRadius={3}
      border={1}
      borderColor="#8769DC"
      sx={{ background: "#f7f5ff", height: "100%" }}
    >
      <Typography variant="h4" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
        {value}
      </Typography>
      <Typography variant="body2" color="textSecondary">
        {label}
      </Typography>
    </Box>
  );
}

function ReportPage() {
  const [leadData, setLeadData] = useState([]);
  const [rawFile, setRawFile] = useState(null);
  const [fileName, setFileName] = useState([]);
  const [csvResponseCorrect, setCsvResponseCorrect] = useState([]);
  const [csvResponseIncorrect, setCsvResponseIncorrect] = useState([]);
  const [loader, setLoader] = useState(false);
  const [hide, setHide] = useState(false);
  const [error, setError] = useState([]);
  const [stats, setStats] = useState(null);

  const STATUS = ["Validate", "Due", "Paid", "Reject", "Update", "Bank_Failed"];
  const [status, setStatus] = useState(STATUS[0]);

  const valid_ref = useRef(null);
  const invalid_ref = useRef(null);

  // let acceptedValidateHeaders = [
  //   "id",
  //   "user_id",
  //   "level",
  //   "action_message",
  //   "action_header",
  //   "conversions",
  //   "amount",
  //   "old",
  // ];
  let baseHeaders = ["id", "user_id"];
  let accepted_headers = {
    Validate: [...baseHeaders, "level" , "base_amount"],
    Paid: [...baseHeaders, "level", "amount","base_amount"],
    Due: [...baseHeaders, "level","base_amount"],
    Reject: [...baseHeaders, "action_header", "action_message"],
    Update: [...baseHeaders, "action_header", "action_message", "status"],
    Bank_Failed: ["user_id", "amount", "reason"],
  };

  useMemo(() => {
    adminAxiosInstance.get("/users").then((res) => {
      if (res.status < 210) {
        let _ = res.data;
        if (!_?.role.includes("admin")) {
          window.location.href = "/";
        }
      }
    });
  }, []);

  const removeQuotes = (str) => str.replaceAll('"', "");

  // Compute upload stats over the ENTIRE file (not just the 50-row preview)
  // so admins can verify the file was read correctly before submitting.
  const computeStats = (rows, cleanHeaders) => {
    const userIdIdx = cleanHeaders.indexOf("user_id");
    const idIdx = cleanHeaders.indexOf("id");
    const amountIdx = cleanHeaders.indexOf("amount");

    const uniqueUsers = new Set();
    let count = 0;
    let totalAmount = 0;
    let referralLeads = 0;
    let normalLeads = 0;

    for (let r of rows.slice(1)) {
      if (!r.trim()) continue;
      const cols = r.split(",");
      count++;

      if (userIdIdx > -1) {
        const u = removeQuotes(cols[userIdIdx] ?? "").trim();
        if (u) uniqueUsers.add(u);
      }
      if (amountIdx > -1) {
        const amt = parseFloat(removeQuotes(cols[amountIdx] ?? "").trim());
        if (!isNaN(amt)) totalAmount += amt;
      }
      if (idIdx > -1) {
        const idVal = removeQuotes(cols[idIdx] ?? "").trim();
        if (idVal.endsWith("_referral")) referralLeads += 1;
        else normalLeads += 1;
      }
    }

    return {
      count,
      uniqueUsers: uniqueUsers.size,
      totalAmount,
      referralLeads,
      normalLeads,
    };
  };

  const parseCsv = (file) => {
    setError([]);
    console.log(import.meta.env.VITE_APP_ENV);
    const filename = file.name;
    setRawFile(file);
    const reader = new FileReader();
    let acceptedHeaders = accepted_headers[status];
    reader.onload = (e) => {
      console.log({ file: e.target });
      const sheet = e.target.result;
      let rows = sheet.includes("\r\n")
        ? sheet.split("\r\n")
        : sheet.split("\n");
      let headers = rows[0].split(",");
      console.log({
        acceptedHeaders,
        headers: headers.map((i) => i.replaceAll('"', "").trim()),
        a: headers.filter(
          (o) => !acceptedHeaders.includes(o.replaceAll('"', "").trim())
        ),
      });

      let incorrectHeaders = headers.filter(
        (o) => !acceptedHeaders.includes(o.replaceAll('"', "").trim())
      );
      if (incorrectHeaders.length) {
        setError([`Incorrect Headers found:\n${incorrectHeaders.join("\n")}`]);
        return;
      }
      if (!filename.toLowerCase().includes(status.toLowerCase())) {
        setError([`File Name does not contain ${status} keyword in it`]);
        return;
      }

      // let missingHeaders = acceptedHeaders.filter(
      //   (o) => !headers.includes(o.replaceAll('"', "").trim())
      // );
      // if (missingHeaders.length) {
      //   setError((err) => [
      //     ...error,
      //     `Missing Headers :\n${missingHeaders.join(" \n")}`,
      //   ]);
      //   return;
      // }
      let data = [];
      // Preview only first 50 rows to keep UI responsive
      for (let r of rows.slice(1, 51)) {
        if (!r.trim()) continue;
        let columns = r.split(",");
        let obj = {};
        for (let col in columns) {
          if (col && col in headers && headers[col]) {
            obj[removeQuotes(headers[col].trim())] = removeQuotes(columns[col]);
          }
        }
        obj = JSON.parse(JSON.stringify(obj));

        data.push(obj);
      }
      const cleanHeaders = headers.map((h) => removeQuotes(h).trim());
      setStats(computeStats(rows, cleanHeaders));
      setFileName(filename);
      setLeadData(data);
    };
    reader.readAsText(file);
  };

  const validateLeads = (source) => {
    if (!leadData.length || !fileName) {
      toast.error("Please select a file first");
      return;
    }

    setLoader(true);
    const formData = new FormData();
    if (rawFile) {
      formData.append('file', rawFile);
      formData.append('status', status.toLowerCase());
      formData.append('fileName', fileName);
    } else {
      formData.append('data', JSON.stringify(leadData));
      formData.append('status', status.toLowerCase());
      formData.append('fileName', fileName);
    }

    adminAxiosInstance
      .post("/reports/validate", formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      })
      .then((response) => {
        setHide(false);
        setLoader(false);
        if (response.status >= 400) {
          alert("Something went wrong in validation, please check CSV");
        } else {
          toast.success("Validation started! Check progress and download results in the 'File Jobs' section.");
          setLeadData([]);
          setRawFile(null);
          setFileName("");
          setStats(null);
        }
      })
      .catch((e) => {
        console.log(e);
        setLoader(false);
        alert("Something went wrong: " + (e.response?.data?.message || e.message));
      });
  };
  useEffect(() => {
    if (csvResponseCorrect.length) {
      console.log(csvResponseCorrect);
      valid_ref.current.link.click();
      toast.success("Downloaded Valid CSV file", {
        position: "bottom-center",
      });
    }
  }, [csvResponseCorrect]);

  useEffect(() => {
    if (csvResponseIncorrect.length) {
      invalid_ref.current.link.click();
      toast.success("Downloaded Invalid CSV file", {
        position: "bottom-center",
      });
    }
  }, [csvResponseIncorrect]);
  const submitLead = (source) => {
    if (!leadData.length || !fileName) {
      toast.error("Please select a file first");
      return;
    }

    setLoader(true);
    
    // Create FormData to send the actual file
    const formData = new FormData();
    // We need the original file object. Let's store it in a new state.
    // For now, if leadData was parsed, we can't easily get the file back unless we stored it.
    // I will modify parseCsv to also store the file object.
    if (rawFile) {
      formData.append('file', rawFile);
      formData.append('status', status.toLowerCase());
      formData.append('fileName', fileName);
    } else {
      // Fallback for direct data (should not happen with new logic)
      formData.append('data', JSON.stringify(leadData));
      formData.append('status', status.toLowerCase());
      formData.append('fileName', fileName);
    }

    adminAxiosInstance
      .post("/reports/leads", formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      })
      .then((response) => {
        setHide(false);
        setLoader(false);
        if (response.status >= 400) {
          alert("Something went wrong in saving report, please check CSV");
        } else {
          toast.success("Report upload started successfully! You can track progress in File Jobs.");
          setLeadData([]);
          setRawFile(null);
          setFileName("");
          setStats(null);
        }
      })
      .catch((e) => {
        console.log(e);
        setLoader(false);
        alert("Something went wrong: " + (e.response?.data?.message || e.message));
      });
  };

  return (
    <PageContainer
      title="Upload Reports"
      description="this is Products Creation page for admins"
    >
      <Container sx={{pt:0}}>
        <Grid container sx={{pt:0}}>
          <Grid item xs={12}>
            <Typography variant="h4">Upload Leads Report here</Typography>
          </Grid>
          {loader ? (
            <Stack alignItems={"center"} justifyContent={"center"}>
              <TailSpin
                height={45}
                width={45}
                style={{ margin: "auto", bg: "#444" }}
              />
            </Stack>
          ) : !leadData.length ? (
            <Grid item xs={12}>
              <Box boxShadow={5} borderRadius={5}>
                <Grid item xs={12}>
                  <Stack
                    direction="column"
                    spacing={1}
                    my={1}
                    p={2}
                    alignItems="center"
                    border={1}
                    borderColor={"#8769DC"}
                    borderRadius={5}
                  >
                    <Stack
                      direction={"row"}
                      alignItems={"flex-start"}
                      selfAlign={"flex-start"}
                      justifyContent={"space-between"}
                      spacing={2}
                      sx={{ width: "79%" }}
                    >
                      <FormControl sx={{ flex: 1 }}>
                        <InputLabel id="category-label">Status</InputLabel>
                        <Select
                          required
                          labelId="report-status"
                          id="report-status"
                          label="Status"
                          sx={{ flex: 2 }}
                          value={status}
                          onChange={(e) => setStatus(e.target.value)}
                        >
                          {STATUS.map((item) => {
                            return (
                              <MenuItem key={item} value={item}>
                                {item}
                              </MenuItem>
                            );
                          })}
                        </Select>
                      </FormControl>
                      <CSVLink
                        label="Download Sample CSV"
                        filename={`sample_${status}_report.csv`}
                        style={{ marginTop: "10px" }}
                        data={[[...accepted_headers[status]]]}
                      >
                        Download Sample
                      </CSVLink>
                    </Stack>
                    <label
                      htmlFor="LeadReport"
                      style={{
                        width: "80%",
                        background: "#eee",
                        borderRadius: "10px",
                        height: "180px",
                        display: "flex",
                        flexFlow: "column",
                        alignItems: "center",
                      }}
                    >
                      <Stack
                        direction="column"
                        spacing={2}
                        alignItems={"center"}
                        my={2}
                        py={3}
                      >
                        <h2>Upload {status} Lead Report (CSV) here</h2>
                        <span>
                          <svg
                            width="16"
                            height="16"
                            viewBox="0 0 16 16"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path
                              fillRule="evenodd"
                              clipRule="evenodd"
                              d="M1.99967 9.33337C2.36786 9.33337 2.66634 9.63185 2.66634 10V12.6667C2.66634 12.8435 2.73658 13.0131 2.8616 13.1381C2.98663 13.2631 3.1562 13.3334 3.33301 13.3334H12.6663C12.8431 13.3334 13.0127 13.2631 13.1377 13.1381C13.2628 13.0131 13.333 12.8435 13.333 12.6667V10C13.333 9.63185 13.6315 9.33337 13.9997 9.33337C14.3679 9.33337 14.6663 9.63185 14.6663 10V12.6667C14.6663 13.1971 14.4556 13.7058 14.0806 14.0809C13.7055 14.456 13.1968 14.6667 12.6663 14.6667H3.33301C2.80257 14.6667 2.29387 14.456 1.91879 14.0809C1.54372 13.7058 1.33301 13.1971 1.33301 12.6667V10C1.33301 9.63185 1.63148 9.33337 1.99967 9.33337Z"
                              fill="#3C50E0"
                            />
                            <path
                              fillRule="evenodd"
                              clipRule="evenodd"
                              d="M7.5286 1.52864C7.78894 1.26829 8.21106 1.26829 8.4714 1.52864L11.8047 4.86197C12.0651 5.12232 12.0651 5.54443 11.8047 5.80478C11.5444 6.06513 11.1223 6.06513 10.8619 5.80478L8 2.94285L5.13807 5.80478C4.87772 6.06513 4.45561 6.06513 4.19526 5.80478C3.93491 5.54443 3.93491 5.12232 4.19526 4.86197L7.5286 1.52864Z"
                              fill="#3C50E0"
                            />
                            <path
                              fillRule="evenodd"
                              clipRule="evenodd"
                              d="M7.99967 1.33337C8.36786 1.33337 8.66634 1.63185 8.66634 2.00004V10C8.66634 10.3682 8.36786 10.6667 7.99967 10.6667C7.63148 10.6667 7.33301 10.3682 7.33301 10V2.00004C7.33301 1.63185 7.63148 1.33337 7.99967 1.33337Z"
                              fill="#3C50E0"
                            />
                          </svg>
                        </span>
                        <p>
                          <span className="text-primary">
                            Click to upload or drag and drop
                          </span>
                        </p>
                      </Stack>
                      <TextField
                        sx={{
                          display: "none",
                          paddingY: "45px",
                          width: "100%",
                        }}
                        id="LeadReport"
                        label="Lead CSV"
                        variant="outlined"
                        type="file"
                        value={leadData || []}
                        inputProps={{
                          accept: ".csv",
                        }}
                        onChange={(e) => parseCsv(e.target.files[0])}
                      />
                    </label>
                  </Stack>
                </Grid>
              </Box>
              {error.length ? (
                <Box boxShadow={5} borderRadius={5}>
                  <Typography p={4}>{error}</Typography>
                </Box>
              ) : null}
            </Grid>
          ) : (
            <Grid item xs={12} my={3}>
              <Typography variant="h4">Leads Report: {status} (Preview - First 50 rows)</Typography>
              {rawFile && rawFile.size > 1024 * 50 && (
                <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
                  Note: For large files, only the first 50 rows are shown as a preview. All records will be processed upon submission.
                </Typography>
              )}
              {stats && (
                <Box
                  boxShadow={3}
                  borderRadius={4}
                  p={2}
                  my={2}
                  sx={{ background: "#fff" }}
                >
                  <Typography variant="subtitle2" color="textSecondary" sx={{ mb: 1 }}>
                    File Stats
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={6} sm={4} md={3}>
                      <StatCard label="Total Entries" value={stats.count.toLocaleString("en-IN")} />
                    </Grid>
                    <Grid item xs={6} sm={4} md={3}>
                      <StatCard label="Unique Users" value={stats.uniqueUsers.toLocaleString("en-IN")} />
                    </Grid>
                    {status === "Paid" && (
                      <Grid item xs={6} sm={4} md={3}>
                        <StatCard
                          label="Total Amount"
                          value={`₹${stats.totalAmount.toLocaleString("en-IN")}`}
                        />
                      </Grid>
                    )}
                    {(status === "Due" || status === "Paid") && (
                      <>
                        <Grid item xs={6} sm={4} md={3}>
                          <StatCard
                            label="Referral Leads"
                            value={stats.referralLeads.toLocaleString("en-IN")}
                          />
                        </Grid>
                        <Grid item xs={6} sm={4} md={3}>
                          <StatCard
                            label="Normal Leads"
                            value={stats.normalLeads.toLocaleString("en-IN")}
                          />
                        </Grid>
                      </>
                    )}
                  </Grid>
                </Box>
              )}
                <Table
                  stickyHeader={true}
                  sx={{ borderBottom: "1px solid #777a" }}
                >
                  <TableHead sx={{}}>
                    <TableRow>
                      {Object.keys(leadData[0]).map((row, index) => {
                        return (
                          <TableCell
                            key={index}
                            sx={{ borderBottom: "1px solid " }}
                            component={"th"}
                          >
                            {row}
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {leadData.map((row) => {
                      return (
                        <TableRow sx={{ border: "1px solid " }}>
                          {Object.values(row).map((col, index) => {
                            return <TableCell key={index}>{col}</TableCell>;
                          })}
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
                <Stack
                  direction={"row"}
                  justifyContent={"center"}
                  spacing={4}
                  my={3}
                  sx={{position:'sticky', bottom:0, background:'#fff', py:'10px', borderTop:'1px solid', borderRadius:'12px'}}
                >
                  <Button
                    size={"large"}
                    variant={"outlined"}
                    sx={{ margin: "0 50%" }}
                    onClick={() => {
                      setLeadData([]);
                      setFileName("");
                      setStats(null);
                    }}
                  >
                    Cancel
                  </Button>
                  {!hide ? (
                    <Button
                      size={"large"}
                      variant={"contained"}
                      sx={{ margin: "0 50%" }}
                      onClick={() => {
                        setHide(true);
                        submitLead();
                      }}
                    >
                      Submit
                    </Button>
                  ) : null}
                </Stack>
              
            </Grid>
          )}
          {
            //   csvResponse && csvResponse.length ? (
            //   <Grid item xs={12} my={3}>
            //     <TableContainer
            //       component={Box}
            //       boxShadow={15}
            //       borderRadius={7}
            //       sx={{ overflow: "hidden" }}
            //     >
            //       <Table
            //         stickyHeader={true}
            //         sx={{ borderBottom: "1px solid #777a" }}
            //       >
            //         <TableHead sx={{}}>
            //           <TableRow>
            //             {Object.keys(csvResponse[0]).map((row, index) => {
            //               return (
            //                 <TableCell
            //                   key={index}
            //                   sx={{ borderBottom: "1px solid " }}
            //                   component={"th"}
            //                 >
            //                   {row}
            //                 </TableCell>
            //               );
            //             })}
            //           </TableRow>
            //         </TableHead>
            //         <TableBody>
            //           {csvResponse.map((row) => {
            //             return (
            //               <TableRow sx={{ border: "1px solid " }}>
            //                 {Object.values(row).map((col, index) => {
            //                   return <TableCell key={index}>{col}</TableCell>;
            //                 })}
            //               </TableRow>
            //             );
            //           })}
            //         </TableBody>
            //       </TableHead>
            //       <Stack
            //         direction={"row"}
            //         justifyContent={"center"}
            //         spacing={4}
            //         my={3}
            //       >
            //         <Button
            //           size={"large"}
            //           variant={"outlined"}
            //           sx={{ margin: "0 50%" }}
            //           onClick={() => {
            //             setLeadData([]);
            //             setFileName("");
            //           }}
            //         >
            //           Cancel
            //         </Button>
            //         {!hide ? (
            //           <Button
            //             size={"large"}
            //             variant={"contained"}
            //             sx={{ margin: "0 50%" }}
            //             onClick={() => {
            //               setHide(true);
            //               type === "submit" ? submitLead() : validateLeads();
            //             }}
            //           >
            //             Submit
            //           </Button>
            //         ) : null}
            //       </Stack>
            //     </TableContainer>
            //   </Grid>
            // ) : null
          }
          <CSVLink
            ref={valid_ref}
            data={csvResponseCorrect}
            filename={`${
              fileName || `${status}_${new Date().toDateString()}`
            }_${status}_valid.csv`}
            onClick={(event) => {
              event.stopPropagation(); // Prevent button click propagation
            }}
            style={{
              display: "none",
            }}
          />
          <CSVLink
            ref={invalid_ref}
            data={csvResponseIncorrect}
            filename={`${
              fileName || `${status}_${new Date().toDateString()}`
            }_${status}_invalid.csv`}
            onClick={(event) => {
              event.stopPropagation(); // Prevent button click propagation
            }}
            style={{
              display: "none",
            }}
          />
        </Grid>
      </Container>
      <ToastContainer />
    </PageContainer>
  );
}

export default ReportPage;
